
import { initializeApp } from "firebase/app";
import { initializeAuth, getAuth, browserLocalPersistence, browserPopupRedirectResolver } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getFunctions } from "firebase/functions";
import { getRemoteConfig } from "firebase/remote-config";
import { Capacitor } from '@capacitor/core';
import { initAppCheck } from './appCheck';

// Get environment variables - Vite makes these available via import.meta.env
// Use fallback values if env vars are not set (for admin app compatibility)
const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
const authDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN;
const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;

// Validate required Firebase config
if (!apiKey || apiKey === 'undefined' || !authDomain || authDomain === 'undefined' || !projectId || projectId === 'undefined') {
    const errorMsg = `Firebase configuration is missing. Please ensure you have a .env file in your app directory with:
VITE_FIREBASE_API_KEY
VITE_FIREBASE_AUTH_DOMAIN
VITE_FIREBASE_PROJECT_ID
VITE_FIREBASE_STORAGE_BUCKET
VITE_FIREBASE_MESSAGING_SENDER_ID
VITE_FIREBASE_APP_ID
VITE_FIREBASE_MEASUREMENT_ID`;
    console.error(errorMsg);
    throw new Error('Firebase configuration is missing. Check console for details.');
}

const firebaseConfig = {
    apiKey: apiKey,
    authDomain: authDomain,
    databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
    projectId: projectId,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

const app = initializeApp(firebaseConfig);

// App Check — PROD ONLY (VITE_APP_ENV === 'production'). Tokens attach to
// Auth/Firestore/Functions traffic so the backend can distinguish real app instances
// from scripted abuse (monitoring mode for now).
// Native: App Attest (iOS) / Play Integrity (Android) bridged via @capacitor-firebase/app-check.
// Web: reCAPTCHA Enterprise when VITE_RECAPTCHA_SITE_KEY is set.
// The init is deferred + timeout-guarded so it can NEVER block auth/data flows — see
// appCheck.ts for the iOS-Chrome reCAPTCHA-hang history that mandates this design.
initAppCheck(app);

// Use browserLocalPersistence everywhere — indexedDB persistence (Firebase default via getAuth)
// hangs in iOS WebKit after email redirect chains, causing onAuthStateChanged to never fire.
// browserLocalPersistence (localStorage) is reliable across all browsers and survives tab closes.
// On web, we also need browserPopupRedirectResolver for social login popups (Google/Apple).
let auth;
try {
    auth = initializeAuth(app, {
        persistence: browserLocalPersistence,
        ...(Capacitor.isNativePlatform() ? {} : { popupRedirectResolver: browserPopupRedirectResolver }),
    });
} catch (e: unknown) {
    // If auth was already initialized (e.g. HMR or multiple imports), fall back to getAuth
    if (e instanceof Error && e.message.includes('already-initialized')) {
        auth = getAuth(app);
    } else {
        throw e;
    }
}
const db = getFirestore(app);
const storage = getStorage(app);
const functions = getFunctions(app);
const remoteConfig = getRemoteConfig(app);

// In-app defaults (used before fetch, or if fetch fails)
remoteConfig.defaultConfig = {
  goals_enabled: 'true',
  subscriptions_enabled: 'false',
  payments_enabled: 'false',
  cart_companion_enabled: 'false',
  /** Ship server-side insight delivery first; set to true in Remote Config when ready to show in home. */
  insights_feed_enabled: 'false',
  debt_manager_enabled: 'false',
  no_spend_weekend_enabled: 'false',
  no_spend_live_activity_enabled: 'false',
  no_spend_android_reminder_enabled: 'false',
  where_it_goes_enabled: 'false',
  /**
   * Version floors default to EMPTY, which means "inert". These must never
   * default to a real version: defaultConfig is what a client falls back to
   * when the Remote Config fetch fails, and a non-empty hard floor here would
   * wall users off every time the network hiccups. Empty = no gate. The gate
   * only ever fires on a value we deliberately set in the console.
   */
  min_ios_version: '',
  min_android_version: '',
  force_update_below_ios: '',
  force_update_below_android: '',
};

// Production: 5 minutes (Firebase default is 12 hours, too long for config changes to propagate)
// Development: 1 minute for rapid iteration
remoteConfig.settings.minimumFetchIntervalMillis = import.meta.env?.DEV ? 60000 : 300000;

import { GoogleAuthProvider, OAuthProvider } from "firebase/auth";

const googleProvider = new GoogleAuthProvider();
const appleProvider = new OAuthProvider('apple.com');

/**
 * Temporarily suppresses Firestore permission-denied console errors during logout.
 * When auth is revoked, all active snapshot listeners fire permission-denied errors
 * simultaneously — this is expected and not actionable.
 */
const originalConsoleError = console.error;
let _isLoggingOut = false;

/** True during the brief logout window when permission-denied listener errors are expected. */
export const isLoggingOut = () => _isLoggingOut;

export const setLoggingOut = (value: boolean) => {
    _isLoggingOut = value;
    if (value) {
        console.error = (...args: unknown[]) => {
            const msg = args.map(a => String(a)).join(' ');
            if (msg.includes('permission-denied') || msg.includes('Missing or insufficient permissions')) return;
            originalConsoleError.apply(console, args);
        };
    } else {
        console.error = originalConsoleError;
    }
};

export { app, auth, db, storage, functions, remoteConfig, googleProvider, appleProvider };
